import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  Wrapper,
  Header,
  Title,
  Footer,
  SeeMoreButton,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableBodyRow,
  TableBodyCell,
  NoDataAvailable,
  SeeEpisodesButtonWrapper,
} from '@insights_module/components/overview_screen/TrendingStyledComponents';
import {
  ContextualDropdownOption,
  findOptionByValue,
  getOptions,
  IabCategoriesDropdown,
} from '@insights_module/components/ContextualDropdown';
import { DateRange, useOverviewContext } from '@insights_module/context/OverviewContext';
import { Loader } from '@components/Loader';
import { Option } from '@components/Dropdown';
import { IAB_CONTEXTUAL_CATEGORIES } from '@insights_module/constants/contextualAndEntity';
import { Modal } from '@components/Modal';
import usePagination from '@app/hooks/usePagination';
import { Pagination } from '@components/Pagination';
import { Button } from '@components/Button';
import { DateIntervalSelection } from '@insights_module/components/overview_screen/DateIntervalSelection';
import { RankedContextualSummary, useOverviewData } from '@app/hooks/useOverviewData';
import { TIER_MAPPER } from '@insights_module/components/ContextualTierBadge';
import { OpenInNewTabIcon } from '@icons/OpenInNewTabIcon';
import { objectToToken } from '@utils/commonUtils';
import { AQLContextualIABQuery, AQLPublishedDateQuery } from '@insights_module/adc_ql/queries';
import { IABFilterType } from '@insights_module/types/ContextualAndEntity';

const dropdownContextualOptions = getOptions(IAB_CONTEXTUAL_CATEGORIES, 1);

const Label = ({ label }: { label: string }) => (
  <Option tw='text-text-and-icon-muted opacity-50'>{label}</Option>
);

const emptyContextualOption: ContextualDropdownOption = {
  value: null,
  label: <Label label='Choose Category' />,
  tierLevel: 0,
  path: [],
};

export const TrendingTopCategoriesList = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const { episodesCount, contextualSearchTerm, setContextualSearchTerm } = useOverviewContext();
  const { top10Categories, rankedCategories, isLoadingCategories } = useOverviewData();
  const [selectedCategory, setSelectedCategory] = useState<ContextualDropdownOption | null>(
    findOptionByValue(dropdownContextualOptions, contextualSearchTerm)
  );

  useEffect(() => {
    if (selectedCategory) {
      setContextualSearchTerm(selectedCategory.value || '');
    }
  }, [selectedCategory]);

  const {
    currentPage,
    paginationRange,
    currentPageData,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    totalPages,
  } = usePagination<RankedContextualSummary>({
    data: rankedCategories,
    itemsPerPage: 19,
    filter: JSON.stringify({ contextualSearchTerm, selectedCategory }),
  });

  const CategoriesModal = (
    <Modal tw='' onClose={() => setShowModal(false)} overlayStyles={tw`bg-[#00000080] `}>
      <Wrapper tw='lg:min-w-[800px] max-w-[800px] bg-neutral-background p-0'>
        <Title tw='text-[16px] text-left p-[12px] border-b border-b-gray-2 w-full'>
          {t('insights.overview_page.trending_section.top_categories.modal_title')}
        </Title>

        <div tw='flex flex-col gap-[10px] px-[12px]'>
          <Header tw='justify-start gap-[8px]'>
            <IabCategoriesDropdown
              cancelable={true}
              options={dropdownContextualOptions}
              emptyOption={emptyContextualOption}
              autoFocus={false}
              searchPlaceholder={t(
                'insights.overview_page.trending_section.top_categories.search_placeholder'
              )}
              value={selectedCategory}
              onChange={(val) => setSelectedCategory(val as ContextualDropdownOption)}
              tw='w-[200px] shadow-none text-text-and-icon'
            />
            <DateIntervalSelection resultType='categories' tw='pl-0' />
          </Header>

          <CategoriesTable
            totalEpisodesCount={episodesCount}
            categories={currentPageData}
            isLoading={isLoadingCategories}
          />
        </div>

        <Footer tw='justify-between p-[12px] border-t border-t-gray-2'>
          {
            <div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                paginationRange={paginationRange}
                onPageChange={goToPage}
                onNext={goToNextPage}
                onPrevious={goToPreviousPage}
              />
            </div>
          }
          <Button.Primary
            onClick={() => {
              setShowModal(false);
            }}>
            {t('common.close')}
          </Button.Primary>
        </Footer>
      </Wrapper>
    </Modal>
  );

  return (
    <Wrapper>
      <div tw='flex flex-col gap-[10px]'>
        <Header>
          <Title>{t('insights.overview_page.trending_section.top_categories.title')}</Title>
          <IabCategoriesDropdown
            cancelable={true}
            options={dropdownContextualOptions}
            emptyOption={emptyContextualOption}
            autoFocus={false}
            searchPlaceholder={t(
              'insights.overview_page.trending_section.top_categories.search_placeholder'
            )}
            value={selectedCategory}
            onChange={(val) => setSelectedCategory(val as ContextualDropdownOption)}
            tw='w-[200px] shadow-none text-text-and-icon'
          />
        </Header>

        <CategoriesTable
          totalEpisodesCount={episodesCount}
          categories={top10Categories}
          isLoading={isLoadingCategories}
        />
      </div>

      <Footer>
        <SeeMoreButton
          onClick={() => {
            setShowModal(true);
          }}>
          {t('insights.overview_page.trending_section.top_categories.see_more')}
        </SeeMoreButton>
      </Footer>

      {showModal && CategoriesModal}
    </Wrapper>
  );
};

type CategoriesTableProps = {
  totalEpisodesCount: number;
  categories: RankedContextualSummary[];
  isLoading: boolean;
};

const SeeEpisodesButton = ({
  category,
  selectedDateRange,
}: {
  category: RankedContextualSummary;
  selectedDateRange: DateRange;
}) => {
  const handleClick = () => {
    const token = objectToToken({
      all: [
        new AQLContextualIABQuery('includes', category.display_name as IABFilterType),
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate || '',
          lte: selectedDateRange.maxDate || '',
        }),
      ],
    });
    const url = `/insights/episodes?query_token=${token}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <SeeEpisodesButtonWrapper onClick={handleClick} title='See Episodes'>
      <OpenInNewTabIcon width={14} height={14} />
    </SeeEpisodesButtonWrapper>
  );
};

const CategoriesTable = ({ totalEpisodesCount, categories, isLoading }: CategoriesTableProps) => {
  const { t } = useTranslation();
  const { selectedDateRange } = useOverviewContext();

  return (
    <Table>
      <TableHeader tw='grid-cols-[30px, 30px, 2fr, 1fr, 1fr, 40px]'>
        <TableHeaderCell>
          {t('insights.overview_page.trending_section.top_categories.table.rank')}
        </TableHeaderCell>
        <TableHeaderCell>
          {' '}
          {t('insights.overview_page.trending_section.top_categories.table.tier')}{' '}
        </TableHeaderCell>
        <TableHeaderCell>
          {' '}
          {t('insights.overview_page.trending_section.top_categories.table.categories')}{' '}
        </TableHeaderCell>
        <TableHeaderCell tw='justify-end'>
          {t('insights.overview_page.trending_section.top_categories.table.number_of_episodes')}
        </TableHeaderCell>
        <TableHeaderCell tw='justify-end'>
          {t('insights.overview_page.trending_section.top_categories.table.percent_of_content')}
        </TableHeaderCell>
      </TableHeader>

      {isLoading ? (
        <Loader size='md' theme='dark' />
      ) : (
        <TableBody>
          {categories?.map((category) => (
            <TableBodyRow key={category.id} tw='grid-cols-[30px, 30px, 2fr, 1fr, 1fr, 40px]'>
              <TableBodyCell>{category.originalIndex + 1}</TableBodyCell>
              <TableBodyCell>
                {TIER_MAPPER[category.taxonomy[0].tier as keyof typeof TIER_MAPPER]}
              </TableBodyCell>
              <CategoryNameCell categoryPath={category.path} />
              <TableBodyCell tw='justify-end'>
                {category.audio_count.toLocaleString()}
              </TableBodyCell>
              <TableBodyCell tw='justify-end'>
                {((category.audio_count / totalEpisodesCount) * 100).toFixed(1)}%
              </TableBodyCell>

              <TableBodyCell tw='justify-end'>
                <SeeEpisodesButton category={category} selectedDateRange={selectedDateRange} />
              </TableBodyCell>
            </TableBodyRow>
          ))}

          {!categories?.length && (
            <NoDataAvailable>
              {' '}
              {t('insights.overview_page.trending_section.top_categories.table.no_data')}{' '}
            </NoDataAvailable>
          )}
        </TableBody>
      )}
    </Table>
  );
};

const CategoryNameCell = ({ categoryPath }: { categoryPath: string[] }) => {
  const title = categoryPath.length === 1 ? categoryPath[0] : categoryPath.join(' > ');

  return (
    <TableBodyCell tw='truncate' title={title}>
      {categoryPath.length === 1 && categoryPath[0]}
      {categoryPath.length === 2 && (
        <div tw='flex gap-[4px]'>
          <div tw='font-normal text-text-and-icon-muted'>{categoryPath[0]}</div>
          <div tw='font-normal'>{'>'}</div>
          <div tw='font-semibold'>{categoryPath[1]}</div>
        </div>
      )}
      {categoryPath.length > 2 && (
        <div tw='flex gap-[4px]'>
          <div tw='font-normal text-text-and-icon-muted'>{categoryPath[0]}</div>
          <div tw='font-normal text-text-and-icon-muted'>{'>'}</div>
          <div tw='font-normal text-text-and-icon-muted'>{'...'}</div>
          <div tw='font-normal'>{'>'}</div>
          <div tw='font-semibold'>{categoryPath[categoryPath.length - 1]}</div>
        </div>
      )}
    </TableBodyCell>
  );
};
